import type { ReactNode } from "react";
import { ProtocolError } from "@snowiespace/protocol";
import { StateNotice, TextField } from "@snowiespace/ui/web";

export type JsonObject = Record<string, unknown>;

export function object(value: unknown): JsonObject {
  return value && typeof value === "object" && !Array.isArray(value) ? value as JsonObject : {};
}

export function stringValue(value: unknown): string | undefined {
  return typeof value === "string" && value !== "" ? value : undefined;
}

export function numberValue(value: unknown): number | undefined {
  return typeof value === "number" && Number.isFinite(value) ? value : undefined;
}

export function message(error: unknown): string {
  return error instanceof Error ? error.message : "Something went wrong.";
}

export function Field({ label, value, onChange, type = "text", placeholder, textarea = false, hint, disabled = false }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  placeholder?: string;
  textarea?: boolean;
  hint?: ReactNode;
  disabled?: boolean;
}) {
  return <TextField label={label} value={value} isDisabled={disabled} type={type} placeholder={placeholder} textarea={textarea} rows={textarea ? 9 : undefined} description={hint} onChange={onChange} />;
}

const conflictCodes = new Set(["CONTENT_CHANGED", "STALE_REVISION", "REVISION_CONFLICT", "SNOWIE_PROFILE_CONFLICT", "LEASE_INVALID", "LEASE_CONFLICT", "IDEMPOTENCY_KEY_REUSED", "SCHEDULE_ACTIVE", "TARGET_HAS_ACTIVE_SCHEDULE", "TARGET_DELETION_IN_PROGRESS"]);
const permissionCodes = new Set(["FORBIDDEN", "MEMBERSHIP_REQUIRED", "CUSTODIAN_REQUIRED", "ACTOR_KIND_DENIED", "CAPABILITY_ACTION_DENIED"]);

export function OperationError({ error, operationKey }: { error: unknown; operationKey?: string }) {
  if (!error) return null;
  const protocol = error instanceof ProtocolError ? error : undefined;
  const code = protocol?.code ?? "OPERATION_FAILED";
  const details = object(protocol?.details);
  const receiptId = stringValue(details.receiptId) ?? stringValue(details.brokerReceiptId);
  if (code.includes("OUTCOME_UNKNOWN") || code === "BROKER_OUTCOME_UNKNOWN") {
    return <StateNotice kind="unknown" title="Outcome needs inspection" code={receiptId ?? operationKey ?? "receipt unavailable"}><p>{message(error)} Do not retry automatically. Inspect the target, then reconcile using the same operation key.</p></StateNotice>;
  }
  if (conflictCodes.has(code)) {
    return <StateNotice kind="conflict" title="Your draft is preserved" code={code}><p>{message(error)} Reread the current version, reconcile it with this draft, and save again under a fresh lease.</p></StateNotice>;
  }
  if (permissionCodes.has(code) || protocol?.status === 403) {
    return <StateNotice kind="permission" title="Authority changed" code={code}><p>{message(error)} Return to an authorized Space destination before attempting another mutation.</p></StateNotice>;
  }
  return <StateNotice kind="error" title="Operation did not complete" code={code}><p>{message(error)}</p></StateNotice>;
}

export async function fileBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = "";
  for (let offset = 0; offset < bytes.length; offset += 0x8000) binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  return btoa(binary);
}

export function downloadBase64(filename: string, mediaType: string, content: string): void {
  const binary = atob(content);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  const url = URL.createObjectURL(new Blob([bytes], { type: mediaType || "application/octet-stream" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function downloadResponse(response: Response, filename: string, mediaType?: string): Promise<void> {
  const blob = await response.blob();
  const value = mediaType && blob.type !== mediaType ? new Blob([blob], { type: mediaType }) : blob;
  const url = URL.createObjectURL(value);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
